// EditorSettingsApi.js - API service for managing user editor settings

/**
 * API service for managing user editor settings in the backend
 */
class EditorSettingsApi {
  constructor(apiBaseUrl, authToken, username = null, userRole = null) {
    this.apiBaseUrl = apiBaseUrl || process.env.REACT_APP_API_BASE_URL || '/api';
    this.authToken = authToken;
    this.username = username;
    this.userRole = userRole;
  }

  /**
   * Set authentication token
   * @param {string} token - JWT authentication token
   */
  setAuthToken(token) {
    this.authToken = token;
  }

  /**
   * Set user context for session-based authentication
   * @param {string} username - Username
   * @param {string} userRole - User role
   */
  setUserContext(username, userRole) {
    this.username = username;
    this.userRole = userRole;
  }

  /**
   * Get request headers with authentication
   * @param {string} username - Optional username override
   * @param {string} userRole - Optional user role override
   * @returns {Object} Headers object
   */
  getHeaders(username = null, userRole = null) {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    // Add session-based auth headers
    const requestingUser = username || this.username;
    const requestingRole = userRole || this.userRole;

    if (requestingUser) {
      headers['Requesting-User'] = requestingUser;
    }
    if (requestingRole) {
      headers['User-Role'] = requestingRole;
    }

    return headers;
  }

  /**
   * Get user's editor settings from the backend
   * @param {string} username - Username to get settings for
   * @returns {Promise<Object>} Editor settings object
   */
  async getUserEditorSettings(username) {
    try {
      const response = await fetch(
        `${this.apiBaseUrl}/user/${encodeURIComponent(username)}/editor-settings`,
        {
          method: 'GET',
          headers: this.getHeaders(),
          credentials: 'include' // Include session cookies for session-based auth
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get editor settings');
      }

      return {
        success: true,
        settings: data.settings,
        message: 'Settings retrieved successfully'
      };

    } catch (error) {
      console.error('Get editor settings error:', error);
      return {
        success: false,
        error: error.message || 'Failed to get editor settings',
        settings: null
      };
    }
  }

  /**
   * Update user's editor settings in the backend
   * @param {string} username - Username to update settings for
   * @param {Object} settings - Editor settings object
   * @returns {Promise<Object>} Result with success status and updated settings
   */
  async updateUserEditorSettings(username, settings) {
    try {
      const response = await fetch(
        `${this.apiBaseUrl}/user/${encodeURIComponent(username)}/editor-settings`,
        {
          method: 'PUT',
          headers: this.getHeaders(),
          body: JSON.stringify({ settings }),
          credentials: 'include' // Include session cookies for session-based auth
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update editor settings');
      }

      return {
        success: true,
        settings: data.settings,
        message: data.message || 'Settings updated successfully'
      };

    } catch (error) {
      console.error('Update editor settings error:', error);
      return {
        success: false,
        error: error.message || 'Failed to update editor settings',
        settings: null
      };
    }
  }

  /**
   * Reset user's editor settings to defaults
   * @param {string} username - Username to reset settings for
   * @returns {Promise<Object>} Result with success status and reset settings
   */
  async resetUserEditorSettings(username) {
    try {
      const response = await fetch(
        `${this.apiBaseUrl}/user/${encodeURIComponent(username)}/editor-settings/reset`,
        {
          method: 'POST',
          headers: this.getHeaders(),
          credentials: 'include' // Include session cookies for session-based auth
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset editor settings');
      }

      return {
        success: true,
        settings: data.settings,
        message: data.message || 'Settings reset to defaults'
      };

    } catch (error) {
      console.error('Reset editor settings error:', error);
      return {
        success: false,
        error: error.message || 'Failed to reset editor settings',
        settings: null
      };
    }
  }

  /**
   * Get default editor settings (client-side defaults)
   * These should match the backend defaults
   * @returns {Object} Default settings object
   */
  getDefaultSettings() {
    return {
      // Output pane placement
      outputPlacement: 'bottom',

      // Appearance & Theme
      theme: 'vs-dark',
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
      fontSize: 14,
      fontLigatures: true,
      lineHeight: 1.5,
      letterSpacing: 0,
      minimapEnabled: false,
      minimapRenderCharacters: true,
      minimapMaxColumn: 120,
      renderWhitespace: 'none',
      renderControlCharacters: false,
      cursorStyle: 'line',
      cursorBlinking: 'blink',

      // Editor Behavior
      wordWrap: 'on',
      wordWrapColumn: 80,
      lineNumbers: 'on',
      tabSize: 4,
      insertSpaces: true,
      autoClosingBrackets: 'always',
      autoClosingQuotes: 'always',
      autoClosingDelete: 'auto',
      autoIndent: 'full',
      folding: true,
      foldingHighlight: true,
      codeLens: true,
      quickSuggestions: true,
      parameterHintsEnabled: true,
      acceptSuggestionOnEnter: 'on',

      // Layout & View
      rulers: [],
      scrollBeyondLastLine: false,
      smoothScrolling: true,
      stickyScrollEnabled: false
    };
  }
}

// Create and export a singleton instance
let editorSettingsApiInstance = null;

/**
 * Get or create the EditorSettingsApi singleton instance
 * @param {string} apiBaseUrl - Optional API base URL
 * @param {string} authToken - Optional auth token
 * @param {string} username - Optional username for session auth
 * @param {string} userRole - Optional user role for session auth
 * @returns {EditorSettingsApi} API instance
 */
export const getEditorSettingsApi = (apiBaseUrl = null, authToken = null, username = null, userRole = null) => {
  if (!editorSettingsApiInstance) {
    editorSettingsApiInstance = new EditorSettingsApi(apiBaseUrl, authToken, username, userRole);
  } else {
    if (apiBaseUrl) {
      editorSettingsApiInstance.apiBaseUrl = apiBaseUrl;
    }
    if (username || userRole) {
      editorSettingsApiInstance.setUserContext(username, userRole);
    }
  }

  if (authToken) {
    editorSettingsApiInstance.setAuthToken(authToken);
  }

  return editorSettingsApiInstance;
};

export default EditorSettingsApi;